import React, { useEffect } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { useCase } from '../../hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import Button from 'components/atoms/Button';
import { EIconTypes } from 'constants/Icons';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import {
  StyledActionsRow,
  StyledTableHeaderCell,
  StyledCommentBox,
  StyledCommentBoxText,
  StyledCommentsCell,
  StyledFileCell,
  StyledFileInfo,
  StyledFileLabel,
  StyledFileName,
  StyledFileRow,
  StyledThumbnailImage,
  StyledThumbnailWrapper,
  StyledSectionWrapper,
  StyledTableHeaderRow,
  StyledIconWrapper,
  StyledReviewButton,
} from './styles';
import { Grid, Typography } from '@mui/material';
import { colors } from 'theme/colors';
import { downloadUrlAsFile } from 'utils/files';
import { ICaseFile } from 'models/cases';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate } from 'utils/date';
import { useQuery } from 'hooks/useQuery';
import Icon from 'components/atoms/Icon/Icon';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';

interface IFileItemProps {
  item: ICaseFile;
  isInProgress: boolean;
  showReviewCommets: boolean;
  showWarning: boolean;
  handleReviewComment: () => void;
  handlePreview: (id: string) => void;
  handleDownload: (id: string, filename: string) => void;
  t: any;
}
const FileItem = ({
  item,
  isInProgress,
  showReviewCommets,
  showWarning,
  handlePreview,
  handleDownload,
  handleReviewComment,
  t,
}: IFileItemProps) => {
  const { longDateFormat } = useGlobalProperty();
  return (
    <StyledFileRow container>
      {showWarning && (
        <Grid
          size={{ xs: 'auto', sm: 0.5 }}
          sx={{
            padding: { xs: '8px 8px 8px 12px', sm: '8px' },
            display: 'grid',
            alignItems: 'center',
          }}
        >
          {item.reviewed ? (
            <StyledIconWrapper>
              <Icon type={EIconTypes.checkmark} color={colors.success800} fontSize={16} />
            </StyledIconWrapper>
          ) : (
            <StyledIconWrapper error>
              <Icon
                type={EIconTypes.lightErrorWarning}
                color={colors.error700}
                fontSize={16}
              />
            </StyledIconWrapper>
          )}
        </Grid>
      )}
      <StyledFileCell size={{ xs: 10, sm: 5 }}>
        <StyledThumbnailWrapper>
          <StyledThumbnailImage src={item.thumbnailPresignedUrl} alt={item.name} />
        </StyledThumbnailWrapper>

        <StyledFileInfo>
          <StyledFileLabel>{t('caseManagement.cases.fileName.label')}</StyledFileLabel>
          <StyledFileName>{item.name}</StyledFileName>

          <StyledActionsRow>
            <Button
              tableButton
              variant={EButtonVariants.outlined}
              icon={EIconTypes.view}
              onClick={() => handlePreview(item.uniqueId)}
              id="application_data_view-button"
            />

            <Button
              tableButton
              variant={EButtonVariants.outlined}
              icon={EIconTypes.download}
              onClick={() => handleDownload(item.uniqueId, item.name)}
              id="application_data_download-button"
            />
          </StyledActionsRow>
        </StyledFileInfo>
      </StyledFileCell>
      {showReviewCommets && (
        <StyledCommentsCell size={{ xs: 12, sm: 6.5 }}>
          <StyledCommentBox>
            {item.lastCommentBy ? (
              <Typography variant="body2">
                <b>{t('general.lastUpdate.label')}: </b>
                {`${formatDate(item.lastCommentDate, longDateFormat)} ${t('general.by.label')} ${item.lastCommentAuthorName} (${item.lastCommentAuthorDisplayUniqueId})`}
              </Typography>
            ) : (
              <StyledCommentBoxText>
                {t('caseManagement.cases.reviewCommentFile.text')}
              </StyledCommentBoxText>
            )}
            <div>
              <Button
                onClick={handleReviewComment}
                variant={EButtonVariants.outlined}
                label={
                  isInProgress
                    ? t('caseManagement.cases.reviewComment.label')
                    : t('caseManagement.cases.actions.details.label')
                }
                size={EButtonSizes.small}
                id="application_data_review_comment-button"
              />
            </div>
          </StyledCommentBox>
        </StyledCommentsCell>
      )}
    </StyledFileRow>
  );
};

interface IApplicationDataProps {
  showReviewCommets: boolean;
  showWarning: boolean;
  isFromLE: boolean;
  isInProgress?: boolean;
}

const ApplicationData = ({
  showReviewCommets,
  showWarning,
  isFromLE,
  isInProgress = false,
}: IApplicationDataProps) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { isMobile } = useThemeBreakpoint();
  const { getCaseFilesData, caseFilesData, previewCaseFile, downloadCaseFile } = useCase();
  const { caseId = '', id = '' } = useParams<{ caseId: string; id: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;

  const handlePreview = async (fileId: string) => {
    const url = await previewCaseFile(fileId);
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleDownload = async (fileId: string, filename: string) => {
    const url = await downloadCaseFile(fileId);
    if (url) {
      downloadUrlAsFile(url, filename);
    }
  };

  useEffect(() => {
    if (caseId) {
      getCaseFilesData(caseId);
    }
  }, [caseId]);

  return (
    <StyledSectionWrapper>
      <StyledTableHeaderRow container>
        {showWarning && <Grid size={{ xs: 0.5 }} sx={{ padding: '8px' }}></Grid>}
        <StyledTableHeaderCell size={{ xs: 5 }}>
          <Typography variant="subtitle2" sx={{ color: colors.primary900 }}>
            {t('caseManagement.cases.applicationFile.label')}
          </Typography>
        </StyledTableHeaderCell>

        {showReviewCommets && (
          <StyledTableHeaderCell size={{ xs: 6.5 }}>
            <Typography variant="subtitle2" sx={{ color: colors.primary900 }}>
              {t('caseManagement.cases.comments.label')}
            </Typography>
          </StyledTableHeaderCell>
        )}
      </StyledTableHeaderRow>

      {caseFilesData.map((item) => (
        <FileItem
          key={item.uniqueId}
          t={t}
          item={item}
          showReviewCommets={showReviewCommets}
          showWarning={showWarning}
          isInProgress={isInProgress}
          handlePreview={handlePreview}
          handleDownload={handleDownload}
          handleReviewComment={() => {
            navigate(
              isFromLE
                ? `${ERouteLinks.LegalEntityCaseDetailsReviewCommentPage.replace(
                    ':caseId',
                    caseId,
                  )
                    .replace(':id', id)
                    .replace(':fileId', item.uniqueId)}?type=${type}`
                : ERouteLinks.CaseDetailsReviewCommentPage.replace(':caseId', caseId).replace(
                    ':fileId',
                    item.uniqueId,
                  ),
            );
          }}
        />
      ))}
    </StyledSectionWrapper>
  );
};

export default ApplicationData;
